import { useAuthStore } from '../stores/auth-store';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  auth?: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: { error?: string; message?: string; issues?: unknown },
  ) {
    super(payload.message ?? payload.error ?? `Request failed (${status})`);
  }
}

// Dedupe refresh in flight: se N chiamate API parallele vedono 401 contemporaneamente,
// solo UNA chiama /api/auth/refresh, le altre aspettano lo stesso promise.
// Senza dedupe, il backend rotation revoca la session vecchia al primo refresh, e i
// successivi refresh arrivano con cookie gia revocato -> 401 -> store cleared ->
// utente forzato a re-login. Vedi sessions DB con 8 sessioni create nello stesso
// minuto al primo deploy 21/05.
let refreshInFlight: Promise<string | null> | null = null;

export async function tryRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) {
        useAuthStore.getState().clear();
        return null;
      }
      const data = (await res.json()) as { accessToken: string };
      // Aggiorna store cosi le chiamate successive vedono il nuovo token senza
      // aspettare RequireAuth bootstrap.
      useAuthStore.setState({ accessToken: data.accessToken });
      return data.accessToken;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { auth = true, body, headers, ...rest } = opts;
  const init: RequestInit = {
    ...rest,
    headers: {
      'content-type': 'application/json',
      ...(headers ?? {}),
    },
    credentials: 'include',
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  let accessToken = useAuthStore.getState().accessToken;
  const buildAuthHeader = (token: string | null) => {
    if (auth && token) {
      (init.headers as Record<string, string>)['authorization'] = `Bearer ${token}`;
    }
  };
  buildAuthHeader(accessToken);

  let res = await fetch(`${API_BASE}${path}`, init);
  if (res.status === 401 && auth) {
    accessToken = await tryRefresh();
    if (accessToken) {
      useAuthStore.setState({ accessToken });
      buildAuthHeader(accessToken);
      res = await fetch(`${API_BASE}${path}`, init);
    }
  }

  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
      issues?: unknown;
    };
    throw new ApiError(res.status, payload);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// Download binario (es. CSV/PDF) con auto-refresh token + scarico file nel browser.
// Usa il Content-Disposition del server se presente, altrimenti il fallback fornito.
export async function downloadFile(path: string, fallbackFilename: string): Promise<void> {
  let accessToken = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = {};
  if (accessToken) headers['authorization'] = `Bearer ${accessToken}`;

  let res = await fetch(`${API_BASE}${path}`, { credentials: 'include', headers });
  if (res.status === 401) {
    accessToken = await tryRefresh();
    if (accessToken) {
      useAuthStore.setState({ accessToken });
      headers['authorization'] = `Bearer ${accessToken}`;
      res = await fetch(`${API_BASE}${path}`, { credentials: 'include', headers });
    }
  }
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    throw new ApiError(res.status, payload);
  }

  // Estrai filename da Content-Disposition se presente
  let filename = fallbackFilename;
  const cd = res.headers.get('content-disposition');
  const match = cd?.match(/filename="?([^";]+)"?/i);
  if (match && match[1]) filename = match[1];

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
